import { Link } from 'react-router-dom';
import TutelaLogo from '../components/icons/TutelaLogo';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useServedModel } from '../hooks/useServedModel';
import { IGNIS_MODELS, LIMES_STATE, STATE_DATE, fmtInt, fmtPct1 } from '../lib/siteState';

export default function Sobre() {
  useDocumentMeta(
    'Sobre a Tutela | Proteção ambiental com machine learning',
    'A Tutela é uma plataforma de proteção ambiental baseada em machine learning, um projeto da Neural Forge. Proteger o território com dados, a começar pelo fogo.',
    '/sobre',
  );
  const { rebuiltServed } = useServedModel();
  return (
    <div className="min-h-[calc(100vh-4rem)] bg-ink-950 text-mist">
      <div className="mx-auto max-w-3xl px-6 py-20">
        <div className="mb-6 font-mono text-xs uppercase tracking-[0.28em] text-ember">Sobre</div>

        <h1 className="font-display text-3xl font-bold tracking-tight text-mist sm:text-4xl">
          Proteger o território com dados
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-mist-muted">
          A Tutela é uma plataforma de proteção ambiental construída sobre inteligência artificial e
          aprendizagem automática. Transforma dados públicos em informação útil para quem cuida do
          território.
        </p>

        <div className="mt-14 space-y-12">
          <section className="border-t border-ink-800 pt-10">
            <h2 className="font-display text-2xl font-bold tracking-tight text-mist">O nome</h2>
            <div className="mt-4 space-y-3 leading-relaxed text-mist-muted">
              <p>
                Em latim, <span className="text-mist">tutela</span> significa proteção, guarda e zelo. É
                essa a ideia que nos move: colocar a tecnologia ao serviço da proteção e cuidar do
                território com dados, em vez de agir às cegas.
              </p>
              <p>
                Daí também a nossa assinatura, <span className="text-mist">Stewards of Tomorrow</span>,
                guardiões do amanhã.
              </p>
            </div>
          </section>

          <section className="border-t border-ink-800 pt-10">
            <h2 className="font-display text-2xl font-bold tracking-tight text-mist">
              Um contributo para a sociedade
            </h2>
            <div className="mt-4 space-y-3 leading-relaxed text-mist-muted">
              <p>
                A Tutela foi construída para ser dada à sociedade. Não nasceu como um produto comercial:
                nasceu para ajudar comunidades, autarquias e a proteção civil a antecipar o risco e a agir
                a tempo.
              </p>
              <p>
                Por isso somos honestos sobre o que os modelos conseguem e não conseguem fazer,
                funcionamos como complemento aos sistemas oficiais, e mantemos a informação essencial
                acessível a todos.
              </p>
            </div>
          </section>

          <section className="border-t border-ink-800 pt-10">
            <h2 className="font-display text-2xl font-bold tracking-tight text-mist">Da Neural Forge</h2>
            <div className="mt-4 space-y-3 leading-relaxed text-mist-muted">
              <p>
                A Tutela é um projeto da{' '}
                <a
                  href="https://neuralforge.pt/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-ember hover:brightness-110"
                >
                  Neural Forge
                </a>
                , onde construímos aplicações com inteligência artificial.
              </p>
              <p>
                O IGNIS, dedicado ao risco de incêndio em Portugal, foi o primeiro módulo; o LIMES, para a
                gestão de faixas de combustível nos municípios, é o segundo. Cada módulo é{' '}
                <Link to="/pilotos" className="font-medium text-ember hover:brightness-110">
                  avaliado em pilotos no terreno
                </Link>
                .
              </p>
            </div>
          </section>

          <section className="border-t border-ink-800 pt-10">
            <h2 className="font-display text-2xl font-bold tracking-tight text-mist">
              Onde estamos, em {STATE_DATE}
            </h2>
            <div className="mt-4 space-y-3 leading-relaxed text-mist-muted">
              <p>
                <span className="text-mist">IGNIS.</span> Público em tutela.land desde 1 de julho de 2026,
                com a validação prospetiva desde 19 de maio. O mapa é servido{' '}
                {rebuiltServed
                  ? `pelo modelo reconstruído em 2026 (${IGNIS_MODELS.rebuilt.version}); a série do modelo anterior ficou fechada na Validação no dia da troca.`
                  : `pelo ensemble sazonal (${IGNIS_MODELS.served.version}). Um modelo reconstruído em 2026 corre em sombra desde ${IGNIS_MODELS.rebuilt.shadowSince}: na avaliação de 2026 cobre ${fmtPct1(IGNIS_MODELS.rebuilt.coverage2026.rebuilt)} das ignições nos 5% de células sinalizadas, contra ${fmtPct1(IGNIS_MODELS.rebuilt.coverage2026.served)} do modelo em serviço, e só entra no mapa se passar um portão público de ${IGNIS_MODELS.rebuilt.gate.liveDays} dias ao vivo.`}{' '}
                A AUC de 0,83 que publicámos em 2026 foi retirada em setembro: reavaliada com negativos do
                mesmo mês, cai para 0,50 a 0,56. O que vale é a fração de ignições cobertas, publicada todos
                os dias em{' '}
                <Link to="/ignis/modelos" className="font-medium text-ember hover:brightness-110">Modelos</Link>
                {' '}e{' '}
                <Link to="/ignis/validacao" className="font-medium text-ember hover:brightness-110">Validação</Link>.
              </p>
              <p>
                <span className="text-mist">LIMES.</span> Em produção para Vila Franca de Xira desde{' '}
                {LIMES_STATE.liveSince}, com {LIMES_STATE.namedAccounts} contas nomeadas do município:{' '}
                {fmtInt(LIMES_STATE.unitsEvaluated)} unidades da rede secundária avaliadas,{' '}
                {fmtInt(LIMES_STATE.verificar)} situações a verificar, série semanal de Sentinel-2 com{' '}
                {LIMES_STATE.s2Weeks} semanas. A precisão medida (54% a 74%) é a razão de o vocabulário ser
                "a verificar" e nunca "incumprimento"; a verificação no terreno pelo município ainda não foi
                feita. Detalhe em{' '}
                <Link to="/limes/como-funciona" className="font-medium text-ember hover:brightness-110">Como funciona</Link>.
              </p>
              <p>
                O que não existe: previsão de propagação de fogo ativo (o simulador não passou a sua
                própria validação em julho de 2026 e não é publicado) e qualquer módulo além destes dois.
              </p>
            </div>
          </section>

          <section className="border-t border-ink-800 pt-10">
            <h2 className="font-display text-2xl font-bold tracking-tight text-mist">Autoria e ciência aberta</h2>
            <div className="mt-4 space-y-3 leading-relaxed text-mist-muted">
              <p>
                A Tutela é desenvolvida por{' '}
                <span className="text-mist">Martim Ramos</span> (Neural Forge,{' '}
                <a
                  href="https://orcid.org/0009-0004-3165-8224"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-ember hover:brightness-110"
                >
                  ORCID 0009-0004-3165-8224
                </a>
                ). O método de avaliação do IGNIS está descrito num preprint, ainda não revisto por
                pares:{' '}
                <a
                  href="https://doi.org/10.5281/zenodo.21793139"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-ember hover:brightness-110"
                >
                  doi:10.5281/zenodo.21793139
                </a>
                . As previsões, os produtos oficiais e os fogos reais ficam gravados todos os dias num{' '}
                <a
                  href="https://github.com/neuralforge-pt/tutela-ignis"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-ember hover:brightness-110"
                >
                  arquivo público
                </a>
                , a base da{' '}
                <Link to="/ignis/validacao" className="font-medium text-ember hover:brightness-110">
                  validação
                </Link>
                .
              </p>
            </div>
          </section>
        </div>

        <div className="mt-16 flex flex-wrap items-center gap-4 border-t border-ink-800 pt-8">
          <Link
            to="/ignis"
            className="inline-flex items-center gap-2 rounded-lg bg-ember px-6 py-3 font-semibold text-ink-950 shadow-[0_0_30px_-8px_#F26A4B] transition-all duration-200 hover:brightness-110"
          >
            Conhecer o IGNIS
          </Link>
          <a
            href="https://neuralforge.pt/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg px-5 py-3 font-mono text-sm uppercase tracking-widest text-mist-muted transition-colors hover:text-mist"
          >
            Neural Forge
          </a>
          <span className="ml-auto opacity-60">
            <TutelaLogo className="h-8 w-8" variant="light" />
          </span>
        </div>
      </div>
    </div>
  );
}
